import get from 'lodash.get';
import set from 'lodash.set';

import { emptyArray } from 'ui-components/default-props/empty';
import { FulfillmentPlanOrderGridStatus } from '../../enums/fulfillmentplan-order-grid-status';
import { UPDATE_SALES_ORDER_SHIP_TO } from '../../orders/actions';
import { SHIPMENT, EXTERNAL } from '../../constants/fulfillment-types';

import * as Actions from './entities.actions';
import * as OrderActions from '../orders/orders.actions';
import { FORCE_ADD_TO_BATCH } from '../batches/batches.actions';
import { BULKLOAD_SALES_ORDERS_REQUEST } from '../bulkload/bulkload.actions';
import { MARK_SHIPPED_SUCCESS } from '../mark-as-shipped/mark-as-shipped.actions';
import { SET_HOLD_UNTIL_DATE_REQUEST, OPTIMISTIC_ASSIGN_FULFILLMENT_PLAN_FROM_ORDERS_GRID } from '../orders/orders.actions';
import { CSW_UPDATE_AVAILABLE_SHIPPING_SERVICES, CSW_SAVE_CONFIG_WITH_PAYLOAD } from '../csw/csw.actions';
import { APP_INITIALIZATION } from '../core/base-actions';
import { createLookupHash } from '../../services/hash-service';
import { CustomsService } from '../../services/customs-service';

export const namespace = 'entities';

const entityNames = [
  'salesOrders',
  'fulfillmentPlans',
  'fulfillments',
  'returns',
  'fulfillmentPlanSets',
  'productProfiles',
  'productCategories',
  'products',
  'inventory',
  // For batch processing should be moved later on
  'batches',
  'batchJobs',
  'pickBatches',
];
const mergeEntity = (prevEntities, newEntities, property) => {
  return newEntities?.[property]
    ? {
        ...prevEntities?.[property],
        ...newEntities?.[property],
      }
    : prevEntities?.[property];
};

const mergeEntities = (prevEntities, newEntities) => {
  return {
    ...prevEntities,
    ...newEntities,
    ...entityNames.reduce((acc, name) => {
      return {
        ...acc,
        [name]: mergeEntity(prevEntities, newEntities, name),
      };
    }, {}),
  };
};

const updateInventoryEntities = (prevEntities, newEntities) => {
  const inventoryEntities = newEntities?.inventory || [];

  const filteredInventoryEntities = Object.keys(inventoryEntities).reduce((acc, key) => {
    const inventoryEntity = inventoryEntities[key];
    if (inventoryEntity.inventoryLocationId.toString() !== '-1') {
      return {
        ...acc,
        [key]: inventoryEntity,
      };
    }
    return acc;
  }, {});

  const newInventoryEntities = {
    ...newEntities,
    inventory: filteredInventoryEntities,
  };

  return {
    ...prevEntities,
    ...newEntities,
    ...entityNames.reduce((acc, name) => {
      return {
        ...acc,
        [name]: newInventoryEntities?.[name] ?? {},
      };
    }, {}),
  };
};

const initialState = {
  loading: {},
  requests: {},
  fulfillmentPlans: emptyArray,
  salesOrders: emptyArray,
  fulfillments: emptyArray,
  pickBatches: emptyArray,
};

const patchRatesAndCustoms = (state, action) => {
  const { fulfillmentPlanId, ratePayload, propertiesPayload, customsPayload } = action;

  if (!fulfillmentPlanId || (!ratePayload && !propertiesPayload && !customsPayload)) {
    return state;
  }

  const nextState = { ...state };
  const fulfillmentPlanPath = `fulfillmentPlans.${fulfillmentPlanId}`;

  if (ratePayload) {
    set(nextState, `${fulfillmentPlanPath}.rateSummary.rate`, ratePayload);
  }

  if (propertiesPayload) {
    set(nextState, `${fulfillmentPlanPath}.properties`, propertiesPayload);
  }

  if (customsPayload) {
    set(nextState, `${fulfillmentPlanPath}.customs`, customsPayload);
  }

  return nextState;
};

const updateLoading = (state, { salesOrderIds }, isLoading) => {
  const salesOrderLoading = salesOrderIds.reduce((acc, salesOrderId) => ({ ...acc, [salesOrderId]: isLoading }), {});
  return {
    ...state,
    loading: {
      ...state.loading,
      salesOrders: {
        ...state.loading.salesOrders,
        ...salesOrderLoading,
      },
    },
  };
};

const fiveMinInMilliseconds = 300000;
const pruneRequests = (state) => {
  const now = new Date();
  const { requests } = state;
  const newRequests = { ...requests };

  const requestIds = requests.keys || emptyArray;

  requestIds.forEach((requestId) => {
    const requestMetadata = requests[requestId];
    const { requestTime } = requestMetadata;
    if (now.getMilliseconds() - requestTime.getMilliseconds() > fiveMinInMilliseconds) {
      delete newRequests[requestId];
    }
  });

  return newRequests;
};

const optimisticallyPatchSalesOrder = (state, action) => {
  const { salesOrderId, newSalesOrder } = action;

  return {
    ...state,
    salesOrders: {
      ...state.salesOrders,
      [salesOrderId]: newSalesOrder,
    },
  };
};

const optimisticallyPatchFulfillmentPlans = (state, action) => {
  const { fulfillmentPlanIds, newFulfillmentPlans } = action;

  const updatedFulfillmentPlans = {};
  fulfillmentPlanIds.forEach((fpId) => {
    updatedFulfillmentPlans[fpId] = newFulfillmentPlans.find((fp) => fp.fulfillmentPlanId === fpId) || state.fulfillmentPlans[fpId];
  });
  return {
    ...state,
    fulfillmentPlans: {
      ...state.fulfillmentPlans,
      ...updatedFulfillmentPlans,
    },
  };
};

const actionMap = {
  [Actions.OPTIMISTICALLY_PATCH_SALES_ORDER_ENTITY]: optimisticallyPatchSalesOrder,
  [Actions.OPTIMISTICALLY_PATCH_FULFILLMENT_PLAN_ENTITIES]: optimisticallyPatchFulfillmentPlans,
  [Actions.BULK_LOAD_ENTITIES_REQUEST]: (state, action) => updateLoading(state, action.payload, true),
  [BULKLOAD_SALES_ORDERS_REQUEST]: (state, action) => updateLoading(state, action, true),
  [Actions.ENTITIES_START_SALES_ORDER_LOADING]: (state, action) => updateLoading(state, action, true),
  [Actions.BULK_LOAD_ENTITIES_SUCCESS]: (state, action) => updateLoading(state, action, false),
  [Actions.ENTITIES_LOAD]: (state, action) => {
    const entitiesToLoad = get(action, 'entities', {});

    const salesOrderIds = Object.keys(entitiesToLoad.salesOrders || {});
    const loadingStateUpdated = updateLoading(state, { salesOrderIds }, false);
    const entities = mergeEntities(state, entitiesToLoad);
    return {
      ...loadingStateUpdated,
      ...entities,
    };
  },
  [Actions.INVENTORY_ENTITIES_LOAD]: (state, action) => {
    const entitiesToLoad = get(action, 'entities', {});
    const salesOrderIds = Object.keys(entitiesToLoad.salesOrders || {});
    const loadingStateUpdated = updateLoading(state, { salesOrderIds }, false);
    const entities = updateInventoryEntities(state, entitiesToLoad);
    return {
      ...loadingStateUpdated,
      ...entities,
    };
  },
  [APP_INITIALIZATION]: (state, action) => {
    const { productCategories, productProfiles } = action.initialData;

    return {
      ...state,
      productCategories: createLookupHash(productCategories || [], 'categoryId'),
      productProfiles: createLookupHash(productProfiles || [], 'productProfileId'),
    };
  },
  [Actions.ENTITIES_BULKLOAD]: (state, action) => {
    return actionMap[Actions.ENTITIES_LOAD](state, action);
  },
  [Actions.ENTITIES_PATCH_RATES]: patchRatesAndCustoms,
  [Actions.ENTITIES_PATCH_CUSTOMS]: patchRatesAndCustoms,
  [Actions.SAVE_BULK_REQUEST_METADATA]: (state, action) => {
    const { requestId, requestTime } = action;
    const requests = pruneRequests(state);
    return {
      ...state,
      requests: {
        ...requests,
        [requestId]: {
          requestTime,
        },
      },
    };
  },
  [Actions.UPDATE_FULFILLMENT_PLAN_NOTE_REQUEST]: (state, action) => {
    const { fulfillmentPlanId, note } = action.payload;
    const fulfillmentPlan = state.fulfillmentPlans[fulfillmentPlanId];
    if (!fulfillmentPlan) {
      return state;
    }
    const { notes = [] } = fulfillmentPlan;
    const newNotes = notes.filter((fpNote) => fpNote.type !== note.type);
    if (note.body) {
      newNotes.push(note);
    }
    const newFulfillmentPlan = { ...fulfillmentPlan, notes: newNotes };

    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        [fulfillmentPlanId]: newFulfillmentPlan,
      },
    };
  },
  [OrderActions.RESTORE_FULFILLMENTS_SUCCESS]: (state, { fulfillmentPlanId }) => {
    if (!fulfillmentPlanId) {
      return state;
    }

    const nextState = { ...state };
    const fulfillmentPlanPath = `fulfillmentPlans.${fulfillmentPlanId}`;
    set(nextState, `${fulfillmentPlanPath}.orderGridStatus`, FulfillmentPlanOrderGridStatus.AwaitingShipment.name);
    return nextState;
  },
  [OrderActions.MARK_PAID_SUCCESS]: (state, { salesOrderId }) => {
    if (!salesOrderId) {
      return state;
    }

    const nextState = { ...state };
    Object.keys(state.fulfillmentPlans).forEach((fulfillmentPlanKey) => {
      const fulfillmentPlan = state.fulfillmentPlans[fulfillmentPlanKey];
      const salesOrderIds = fulfillmentPlan.salesOrdersFulfilledIds || [];
      const isFulfillmentPlanAssociatedToSalesOrder = salesOrderIds.some((soId) => soId === salesOrderId);
      if (isFulfillmentPlanAssociatedToSalesOrder) {
        set(
          nextState,
          `fulfillmentPlans['${fulfillmentPlan.fulfillmentPlanId}'].orderGridStatus`,
          FulfillmentPlanOrderGridStatus.AwaitingShipment.name,
        );
      }
    });
    return nextState;
  },
  [MARK_SHIPPED_SUCCESS]: (state, { fulfillmentPlanIds }) => {
    if (!fulfillmentPlanIds) {
      return state;
    }

    const nextState = { ...state };
    fulfillmentPlanIds.forEach((fpId) => {
      set(nextState, `fulfillmentPlans[${fpId}].orderGridStatus`, FulfillmentPlanOrderGridStatus.Shipped.name);
    });
    return nextState;
  },
  [UPDATE_SALES_ORDER_SHIP_TO]: (state, action) => {
    const { salesOrderId, address } = action;
    const { fulfillmentPlanIds = emptyArray } = state.salesOrders[salesOrderId];

    const updateFulfillmentPlanLabelConfig = (fulfillmentPlan) => {
      return {
        ...fulfillmentPlan,
        labelConfiguration: {
          ...fulfillmentPlan.labelConfiguration,
          shipTo: {
            ...address,
          },
        },
      };
    };

    const newFulfillmentPlans = fulfillmentPlanIds.reduce((fulfillmentPlans, fpId) => {
      const fulfillmentPlan = state.fulfillmentPlans[fpId];
      if (fulfillmentPlan.orderGridStatus === 'Shipped') {
        return fulfillmentPlans;
      }
      return {
        ...fulfillmentPlans,
        [fpId]: updateFulfillmentPlanLabelConfig(fulfillmentPlan),
      };
    }, {});

    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        ...newFulfillmentPlans,
      },
    };
  },
  // optimistic updates
  [SET_HOLD_UNTIL_DATE_REQUEST]: (state, action) => {
    const { payload } = action;
    const newFulfillmentPlans = {};

    payload.fulfillmentPlanIds.forEach((fpId) => {
      newFulfillmentPlans[fpId] = {
        ...state.fulfillmentPlans[fpId],
        orderGridStatus: FulfillmentPlanOrderGridStatus.OnHold.name,
      };
    });
    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        ...newFulfillmentPlans,
      },
    };
  },
  [OPTIMISTIC_ASSIGN_FULFILLMENT_PLAN_FROM_ORDERS_GRID]: (state, action) => {
    const { payload } = action;
    const newFulfillmentPlans = {};
    newFulfillmentPlans[payload.fulfillmentPlanId] = {
      ...state.fulfillmentPlans[payload.fulfillmentPlanId],
      assignedToUserId: payload.userId,
    };
    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        ...newFulfillmentPlans,
      },
    };
  },
  [FORCE_ADD_TO_BATCH]: (state, action) => {
    const { fulfillmentPlanSetId, fulfillmentPlanIds } = action;
    const existingFulfillmentPlans = fulfillmentPlanIds.map((id) => state.fulfillmentPlans[id]);
    const newFulfillmentPlans = existingFulfillmentPlans.map((plan) => ({
      ...plan,
      fulfillmentPlanSetId,
    }));
    const fulfillmentPlanMap = newFulfillmentPlans.reduce(
      (sum, fulfillmentPlan) => ({
        ...sum,
        [fulfillmentPlan.fulfillmentPlanId]: fulfillmentPlan,
      }),
      {},
    );
    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        ...fulfillmentPlanMap,
      },
    };
  },
  [CSW_SAVE_CONFIG_WITH_PAYLOAD]: (state, action) => {
    const { fulfillmentPlanId, payload } = action;
    const fulfillmentPlan = state.fulfillmentPlans[fulfillmentPlanId];
    if (!fulfillmentPlan) {
      return state;
    }

    const updateShipFromRelatedFields = () => {
      if (
        (payload.shipFromId === undefined && payload.sellerFulfillmentProviderId === undefined) ||
        (payload.shipFromId === fulfillmentPlan.shipFromId &&
          payload.sellerFulfillmentProviderId === fulfillmentPlan.sellerFulfillmentProviderId)
      ) {
        return {};
      }
      if (payload.sellerFulfillmentProviderId) {
        return {
          sellerFulfillmentProviderId: payload.sellerFulfillmentProviderId,
          shipFromId: null,
          fulfillmentType: EXTERNAL,
        };
      }
      return {
        shipFromId: payload.shipFromId,
        sellerFulfillmentProviderId: null,
        fulfillmentType: SHIPMENT,
      };
    };

    const updateProperties = () => {
      if (payload.needsCustoms !== undefined) {
        return {
          ...fulfillmentPlan.properties,
          needsCustoms: payload.needsCustoms,
        };
      }
      return fulfillmentPlan.properties;
    };

    const updateConfiguration = () => {
      const externalFulfillmentConfigurationUpdates = {};

      const isExternalFulfillmentConfiguration = payload.sellerFulfillmentProviderId;
      // allow null for any value which can be "unset" by the user or the system
      if (payload.fulfillmentServiceId || payload.fulfillmentServiceId === null) {
        externalFulfillmentConfigurationUpdates.fulfillmentServiceId = payload.fulfillmentServiceId;
      }
      if (isExternalFulfillmentConfiguration) {
        return {
          ...externalFulfillmentConfigurationUpdates,
          externalFulfillmentConfiguration: {
            ...fulfillmentPlan.externalFulfillmentConfiguration,
            ...externalFulfillmentConfigurationUpdates,
          },
        };
      }

      const labelConfigurationUpdates = {};

      if (payload.insuranceProvider) {
        labelConfigurationUpdates.insuranceProvider = payload.insuranceProvider;
      }

      const hasServiceIdUpdate = payload.serviceId || payload.serviceId === null;
      if (hasServiceIdUpdate) {
        labelConfigurationUpdates.serviceId = payload.serviceId;
      }
      const hasSellerLabelProviderIdUpdate = payload.sellerLabelProviderId || payload.sellerLabelProviderId === null;
      if (hasSellerLabelProviderIdUpdate) {
        labelConfigurationUpdates.sellerLabelProviderId = payload.sellerLabelProviderId;
      }
      const hasServiceCollectionGuidUpdate = payload.serviceCollectionGuid || payload.serviceCollectionGuid === null;
      if (hasServiceCollectionGuidUpdate) {
        labelConfigurationUpdates.serviceCollectionGuid = payload.serviceCollectionGuid;
      }

      if (payload.shipDate || payload.shipDate === null) {
        labelConfigurationUpdates.shipDate = payload.shipDate;
      }

      if (payload.packages) {
        labelConfigurationUpdates.packages = payload.packages;
      }

      if (payload.options) {
        labelConfigurationUpdates.options = payload.options;
      }

      if (payload.pickup || payload.pickup === null) {
        labelConfigurationUpdates.pickup = payload.pickup;
      }

      if (payload.driverTip || payload.driverTip === null) {
        labelConfigurationUpdates.driverTip = payload.driverTip;
      }

      if (payload.customs) {
        const mostRecentNeedsCustoms = payload.needsCustoms !== undefined ? payload.needsCustoms : fulfillmentPlan.properties?.needsCustoms;
        const newCustoms = CustomsService.restoreCustomsToNotNeededState(payload.customs, mostRecentNeedsCustoms);
        labelConfigurationUpdates.customs = newCustoms;
      }

      if (Object.keys(labelConfigurationUpdates).length) {
        return {
          ...labelConfigurationUpdates,
          labelConfiguration: {
            ...fulfillmentPlan.labelConfiguration,
            ...labelConfigurationUpdates,
          },
        };
      }
      return { labelConfiguration: fulfillmentPlan.labelConfiguration };
    };

    const updateTopLevelValues = () => {
      const topLevelUpdates = {};

      if (payload.packingSlipTemplateId || payload.packingSlipTemplateId === null) {
        topLevelUpdates.packingSlipTemplateId = payload.packingSlipTemplateId;
      }

      if (payload.deliveryEmailTemplateId || payload.deliveryEmailTemplateId === null) {
        topLevelUpdates.deliveryEmailTemplateId = payload.deliveryEmailTemplateId;
      }

      if (payload.shipmentEmailNotificationTemplateId || payload.shipmentEmailNotificationTemplateId === null) {
        topLevelUpdates.shipmentEmailNotificationTemplateId = payload.shipmentEmailNotificationTemplateId;
      }

      if (Object.keys(topLevelUpdates).length) {
        return {
          ...topLevelUpdates,
        };
      }
      return {};
    };

    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        [fulfillmentPlanId]: {
          ...fulfillmentPlan,
          ...updateShipFromRelatedFields(),
          properties: updateProperties(),
          ...updateConfiguration(),
          ...updateTopLevelValues(),
        },
      },
    };
  },
  [CSW_UPDATE_AVAILABLE_SHIPPING_SERVICES]: (state, action) => {
    const { fulfillmentPlanId, updatedAvailableShippingServices } = action;
    if (!state.fulfillmentPlans) {
      return state;
    }
    const fulfillmentPlan = state.fulfillmentPlans[fulfillmentPlanId];
    if (!fulfillmentPlan) {
      return state;
    }
    const newFulfillmentPlan = {
      ...fulfillmentPlan,
      availableSellerShippingServices: updatedAvailableShippingServices,
    };
    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        [fulfillmentPlanId]: newFulfillmentPlan,
      },
    };
  },
  [Actions.UPDATE_FULFILLMENT_PLAN_ADDITIONAL_OPTIONS]: (state, action) => {
    const { fulfillmentPlanId, additionalOptions } = action;
    if (!state.fulfillmentPlans) {
      return state;
    }
    const fulfillmentPlan = state.fulfillmentPlans[fulfillmentPlanId];
    if (!fulfillmentPlan) {
      return state;
    }
    const newFulfillmentPlan = {
      ...fulfillmentPlan,
      options: {
        ...fulfillmentPlan.options,
        additionalOptions,
      },
      labelConfiguration: {
        ...fulfillmentPlan.labelConfiguration,
        options: {
          ...fulfillmentPlan.labelConfiguration.options,
          additionalOptions,
        },
      },
    };
    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        [fulfillmentPlanId]: newFulfillmentPlan,
      },
    };
  },
  [Actions.UNLOCK_ADDRESS_REQUEST]: (state, action) => {
    const { fulfillmentPlanIds } = action.payload;
    if (!fulfillmentPlanIds) {
      return { ...state };
    }
    const fulfillmentPlans = {};
    fulfillmentPlanIds.forEach((id) => {
      fulfillmentPlans[id] = {
        ...state.fulfillmentPlans[id],
        lockAddress: false,
      };
    });

    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        ...fulfillmentPlans,
      },
    };
  },
  [Actions.ENTITIES_PATCH_ALTERNATIVE_RATES]: (state, action) => {
    const { orderAlternativeRates } = action?.alternativeRatesPayload || {};
    if (!orderAlternativeRates || !orderAlternativeRates.length) {
      return state;
    }

    const updatedFulfillmentPlans = orderAlternativeRates.reduce((acc, { fulfillmentPlanId, alternativeRates }) => {
      const existingFulfillmentPlan = state.fulfillmentPlans[fulfillmentPlanId];

      if (!existingFulfillmentPlan) {
        return acc;
      }

      return {
        ...acc,
        [fulfillmentPlanId]: {
          ...existingFulfillmentPlan,
          alternativeRates,
        },
      };
    }, {});

    if (!Object.keys(updatedFulfillmentPlans).length) {
      return state;
    }

    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        ...updatedFulfillmentPlans,
      },
    };
  },
  [Actions.DISMISS_FRAUD_INDICATOR_REQUEST]: (state, action) => {
    const { fulfillmentPlanId, fraudIndicatorCode } = action.payload;
    const fulfillmentPlan = state.fulfillmentPlans?.[fulfillmentPlanId];

    if (!fulfillmentPlan?.fraudDetection?.indicators) {
      return state;
    }

    return {
      ...state,
      fulfillmentPlans: {
        ...state.fulfillmentPlans,
        [fulfillmentPlanId]: {
          ...fulfillmentPlan,
          fraudDetection: {
            ...fulfillmentPlan.fraudDetection,
            indicators: fulfillmentPlan.fraudDetection.indicators.filter(
              (indicator) => indicator.fraudIndicatorCode !== fraudIndicatorCode,
            ),
          },
        },
      },
    };
  },
};

export const reduce = (state = initialState, action = {}, optimisticHistory) => {
  const reducer = actionMap[action.type];

  if (reducer) {
    // replay any optimistic update actions to preserve user made changes that are still saving
    const pendingHistoryActions =
      optimisticHistory?.filter((optimisticHistoryEntry) => optimisticHistoryEntry.action.type === CSW_SAVE_CONFIG_WITH_PAYLOAD) || [];

    const finalizedState = pendingHistoryActions.reduce(
      (currentState, optimisticHistoryEntry) => {
        const updateReducer = actionMap[optimisticHistoryEntry.action.type];
        return updateReducer(currentState, optimisticHistoryEntry.action);
      },
      reducer(state, action),
    );
    return finalizedState;
  }

  return state;
};
